import React, { useState } from 'react';
import { HomeAssistantConfig } from '../../types/database.types';
import { 
  getHAConfig, 
  saveHAConfig, 
  testHAConnection, 
  sendHATextToSpeech,
  sendHAiPhonePush,
  DEFAULT_HA_URL,
  LOCAL_HA_URL,
  DEFAULT_HA_TOKEN
} from '../../lib/homeAssistant';
import { registerPushNotifications, testPushReminder } from '../../lib/pushManager';
import { getStoredTheme, applyTheme, ThemePreference } from '../../lib/theme';
import { 
  Sliders, 
  Sun, 
  Moon, 
  Laptop, 
  Radio, 
  Volume2, 
  Link, 
  Save, 
  ShieldCheck, 
  Bell,
  Smartphone,
  CheckCircle2,
  Share2
} from 'lucide-react';

interface SettingsViewProps {
  onThemeChanged: () => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  onThemeChanged,
}) => {
  const [config, setConfig] = useState<HomeAssistantConfig>(getHAConfig());
  const [currentTheme, setCurrentTheme] = useState<ThemePreference>(getStoredTheme());
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const handleSelectTheme = (theme: ThemePreference) => {
    setCurrentTheme(theme);
    applyTheme(theme);
    onThemeChanged();
    setStatusMessage(`Tema alterado para: ${theme === 'system' ? 'Automático (Sistema)' : theme === 'dark' ? 'Modo Escuro (OLED)' : 'Modo Branco (Claro)'}`);
    setTimeout(() => setStatusMessage(null), 2500);
  };

  const handleSave = () => {
    saveHAConfig(config);
    setStatusMessage('Configurações do Home Assistant salvas com sucesso!');
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setStatusMessage('Testando conexão com o Home Assistant...');
    const res = await testHAConnection(config);
    setIsTesting(false);
    setStatusMessage(res.message);
  };

  const handleTestTTS = async () => {
    setStatusMessage('Enviando anúncio de voz aos Echo Dots...');
    const ok = await sendHATextToSpeech(
      'Atenção Eduardo! Teste de integração do Vida nos Echo Dots concluído com sucesso.',
      config
    );
    setStatusMessage(ok ? 'Voz enviada aos Echo Dots com sucesso!' : 'Falha ao enviar TTS. Verifique a URL e Token.');
  };

  const handleTestHAiPhone = async () => {
    setStatusMessage('Enviando notificação de teste para o iPhone via Home Assistant...');
    const ok = await sendHAiPhonePush(
      '💧 Vida: Notificação no iPhone',
      'Conexão com seu iPhone via Home Assistant funcionando perfeitamente!',
      config
    );
    setStatusMessage(ok ? 'Push enviado ao iPhone com sucesso!' : 'Falha ao enviar push. Verifique se o app do Home Assistant está no iPhone.');
  };

  const handleEnablePush = async () => {
    const res = await registerPushNotifications();
    setStatusMessage(res.message);
  };

  const handleTestPush = async () => {
    await testPushReminder('water');
    setStatusMessage('Notificação de teste disparada na tela!');
  };

  return (
    <div className="space-y-4 max-w-xl mx-auto pb-32">
      {/* Header da Aba */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2.5 rounded-[18px] bg-[#0A84FF]/10 text-[#0A84FF] border border-[#0A84FF]/20">
          <Sliders className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-white">Ajustes</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Aparência, Integrações e Notificações</p>
        </div>
      </div>

      {/* Mensagem de Feedback Flutuante */}
      {statusMessage && (
        <div className="p-3.5 rounded-[20px] bg-[#30D6FB]/10 border border-[#30D6FB]/30 text-xs text-slate-900 dark:text-[#30D6FB] font-semibold flex items-center gap-2 animate-fadeIn">
          <ShieldCheck className="w-4 h-4 text-[#30D6FB] shrink-0" />
          <span>{statusMessage}</span>
        </div>
      )}

      {/* SEÇÃO 1: APARÊNCIA */}
      <div className="liquid-glass rounded-[28px] p-5 border border-slate-200 dark:border-white/[0.08] space-y-3 shadow-sm transition-colors duration-200">
        <div className="flex items-center gap-2">
          <Sun className="w-4 h-4 text-[#FF9F0A]" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
            Aparência
          </h3>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          Defina se o visual acompanha as preferências do seu aparelho ou fixe o modo escuro ou claro.
        </p>

        <div className="grid grid-cols-3 gap-2.5 pt-1">
          {/* Sistema */}
          <button
            onClick={() => handleSelectTheme('system')}
            className={`p-4 rounded-[20px] border text-center transition flex flex-col items-center gap-2 cursor-pointer apple-press ${
              currentTheme === 'system'
                ? 'bg-[#0A84FF]/15 border-[#0A84FF] text-[#0A84FF] shadow-sm'
                : 'bg-white/60 dark:bg-black/60 border-slate-200 dark:border-white/[0.06] text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/[0.12]'
            }`}
          >
            <Laptop className="w-5 h-5" />
            <span className="text-xs font-bold">Automático</span>
            <span className="text-[10px] text-slate-400 leading-tight">Segue sistema</span>
          </button>

          {/* Escuro OLED */}
          <button
            onClick={() => handleSelectTheme('dark')}
            className={`p-4 rounded-[20px] border text-center transition flex flex-col items-center gap-2 cursor-pointer apple-press ${
              currentTheme === 'dark'
                ? 'bg-[#0A84FF]/15 border-[#0A84FF] text-[#0A84FF] shadow-sm'
                : 'bg-white/60 dark:bg-black/60 border-slate-200 dark:border-white/[0.06] text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/[0.12]'
            }`}
          >
            <Moon className="w-5 h-5" />
            <span className="text-xs font-bold">Modo Escuro</span>
            <span className="text-[10px] text-slate-400 leading-tight">OLED Black</span>
          </button>

          {/* Branco Limpo */}
          <button
            onClick={() => handleSelectTheme('light')}
            className={`p-4 rounded-[20px] border text-center transition flex flex-col items-center gap-2 cursor-pointer apple-press ${
              currentTheme === 'light'
                ? 'bg-[#0A84FF]/15 border-[#0A84FF] text-[#0A84FF] shadow-sm'
                : 'bg-white/60 dark:bg-black/60 border-slate-200 dark:border-white/[0.06] text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-white/[0.12]'
            }`}
          >
            <Sun className="w-5 h-5 text-[#FF9F0A]" />
            <span className="text-xs font-bold">Modo Branco</span>
            <span className="text-[10px] text-slate-400 leading-tight">Clean</span>
          </button>
        </div>
      </div>

      {/* SEÇÃO 2: INTEGRAÇÕES (HOME ASSISTANT & ECHO DOTS) */}
      <div className="liquid-glass rounded-[28px] p-5 border border-slate-200 dark:border-white/[0.08] space-y-4 shadow-sm transition-colors duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-[#0A84FF]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              Integrações
            </h3>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#0A84FF]/10 text-[#0A84FF] border border-[#0A84FF]/20">
            Home Assistant
          </span>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          Conexão com seu Home Assistant para avisos de voz nos <strong>Echo Dots</strong> e push direto no <strong>iPhone</strong>.
        </p>

        <div className="space-y-3 text-xs">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-slate-700 dark:text-slate-300 font-semibold">
                URL do Home Assistant
              </label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, url: DEFAULT_HA_URL })}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition cursor-pointer ${
                    config.url === DEFAULT_HA_URL
                      ? 'bg-[#0A84FF]/20 text-[#0A84FF] border-[#0A84FF]'
                      : 'bg-slate-100 dark:bg-white/[0.04] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 border-slate-200 dark:border-white/[0.08]'
                  }`}
                >
                  🌐 DuckDNS HTTPS (PWA)
                </button>
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, url: LOCAL_HA_URL })}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition cursor-pointer ${
                    config.url === LOCAL_HA_URL
                      ? 'bg-[#0A84FF]/20 text-[#0A84FF] border-[#0A84FF]'
                      : 'bg-slate-100 dark:bg-white/[0.04] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 border-slate-200 dark:border-white/[0.08]'
                  }`}
                >
                  🏠 IP Local
                </button>
              </div>
            </div>
            <input
              type="text"
              value={config.url}
              onChange={(e) => setConfig({ ...config, url: e.target.value })}
              placeholder={DEFAULT_HA_URL}
              className="w-full px-3.5 py-2.5 rounded-[16px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] text-slate-900 dark:text-white font-mono text-xs focus:border-[#0A84FF] outline-none transition"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-slate-700 dark:text-slate-300 font-semibold">
                Long-Lived Access Token
              </label>
              {config.token !== DEFAULT_HA_TOKEN && (
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, token: DEFAULT_HA_TOKEN })}
                  className="text-[10px] font-semibold text-[#0A84FF] hover:underline cursor-pointer"
                >
                  Usar Token do Servidor
                </button>
              )}
            </div>
            <input
              type="password"
              value={config.token}
              onChange={(e) => setConfig({ ...config, token: e.target.value })}
              placeholder="Cole seu token gerado no Home Assistant"
              className="w-full px-3.5 py-2.5 rounded-[16px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] text-slate-900 dark:text-white font-mono text-xs focus:border-[#0A84FF] outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-slate-700 dark:text-slate-300 font-semibold mb-1">
                Echo Dot da Sala
              </label>
              <input
                type="text"
                value={config.echoDotSala}
                onChange={(e) => setConfig({ ...config, echoDotSala: e.target.value })}
                placeholder="notify.echo_dot_da_sala_announce"
                className="w-full px-3.5 py-2.5 rounded-[16px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] text-slate-900 dark:text-white font-mono text-xs focus:border-[#0A84FF] outline-none transition"
              />
            </div>

            <div>
              <label className="block text-slate-700 dark:text-slate-300 font-semibold mb-1">
                Echo Dot Secundário
              </label>
              <input
                type="text"
                value={config.echoDotOutro}
                onChange={(e) => setConfig({ ...config, echoDotOutro: e.target.value })}
                placeholder="notify.echo_dot_announce"
                className="w-full px-3.5 py-2.5 rounded-[16px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] text-slate-900 dark:text-white font-mono text-xs focus:border-[#0A84FF] outline-none transition"
              />
            </div>
          </div>

          {/* Ações de Teste e Salvar */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={handleTestConnection}
              disabled={isTesting}
              className="py-2.5 px-3 rounded-[16px] bg-slate-100 dark:bg-black/60 hover:bg-slate-200 dark:hover:bg-[#2C2C2E] text-[#0A84FF] border border-slate-200 dark:border-white/[0.08] text-xs font-semibold transition flex items-center justify-center gap-1.5 apple-press"
            >
              <Link className="w-3.5 h-3.5" />
              <span>Testar Conexão</span>
            </button>

            <button
              onClick={handleTestTTS}
              className="py-2.5 px-3 rounded-[16px] bg-slate-100 dark:bg-black/60 hover:bg-slate-200 dark:hover:bg-[#2C2C2E] text-[#0A84FF] border border-slate-200 dark:border-white/[0.08] text-xs font-semibold transition flex items-center justify-center gap-1.5 apple-press"
            >
              <Volume2 className="w-3.5 h-3.5" />
              <span>Echo Dot</span>
            </button>

            <button
              onClick={handleTestHAiPhone}
              className="py-2.5 px-3 rounded-[16px] bg-slate-100 dark:bg-black/60 hover:bg-slate-200 dark:hover:bg-[#2C2C2E] text-[#0A84FF] border border-slate-200 dark:border-white/[0.08] text-xs font-semibold transition flex items-center justify-center gap-1.5 apple-press"
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span>Push iPhone</span>
            </button>

            <button
              onClick={handleSave}
              className="ml-auto py-2.5 px-4 rounded-[16px] bg-[#0A84FF] hover:bg-[#0071E3] text-white text-xs font-bold uppercase tracking-wider transition shadow-sm flex items-center gap-1.5 cursor-pointer apple-press"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Salvar</span>
            </button>
          </div>
        </div>
      </div>

      {/* SEÇÃO 3: NOTIFICAÇÕES VIA HOME ASSISTANT E ALEXA */}
      <div className="liquid-glass rounded-[28px] p-5 border border-slate-200 dark:border-white/[0.08] space-y-3 shadow-sm transition-colors duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#30D6FB]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              Cobranças Home Assistant & Alexa
            </h3>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#30D6FB]/10 text-[#30D6FB] border border-[#30D6FB]/20">
            13 Doses + Treino
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          Os alertas são enviados em segundo plano via <strong>Home Assistant</strong>: voz curta direta no <strong>Echo Dot</strong> e push nativo no seu <strong>iPhone</strong> (das 07:00 às 22:00 para hidratação e às 19:30 para o treino).
        </p>

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleTestTTS}
            className="flex-1 py-3 px-4 rounded-[18px] bg-[#30D6FB] hover:brightness-105 text-black text-xs font-extrabold uppercase tracking-wider transition shadow-sm flex items-center justify-center gap-1.5 cursor-pointer apple-press"
          >
            <Volume2 className="w-4 h-4" />
            <span>Testar Echo Dot</span>
          </button>

          <button
            onClick={handleTestHAiPhone}
            className="py-3 px-4 rounded-[18px] bg-slate-100 dark:bg-black/60 hover:bg-slate-200 dark:hover:bg-[#2C2C2E] border border-slate-200 dark:border-white/[0.08] text-slate-700 dark:text-slate-300 text-xs font-semibold transition apple-press flex items-center gap-1.5"
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Testar Push iPhone</span>
          </button>
        </div>
      </div>
    </div>
  );
};
